/**
 * Payment Schedules Management Component
 * Comprehensive UI for managing invoice payment schedules (échéanciers de paiement)
 */

import React, { useState, useEffect } from 'react';
import { usePaymentSchedules } from '@/hooks/usePaymentSchedules';
import { GenerateSchedulesParams } from '@/utils/paymentSchedulesAPI';
import {
  Calendar,
  Euro,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Trash2,
  RefreshCw,
  Zap,
  X,
} from 'lucide-react';
import { confirm } from '@/stores/confirmStore';
import { logger } from '@/utils/logger';

interface PaymentSchedulesManagementProps {
  invoiceId: string;
  invoiceNumber: string;
  invoiceTotal: number;
  onClose?: () => void;
}

export const PaymentSchedulesManagement: React.FC<PaymentSchedulesManagementProps> = ({
  invoiceId,
  invoiceNumber,
  invoiceTotal,
  onClose,
}) => {
  const {
    schedules: rawSchedules,
    stats,
    loading,
    saving,
    error,
    loadSchedules,
    loadStats,
    generateSchedules,
    markAsPaid,
    deleteSchedule,
    clearError,
    reload,
  } = usePaymentSchedules();

  // ✅ Defensive: ensure schedules is always an array
  const schedules = rawSchedules || [];

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateParams, setGenerateParams] = useState<GenerateSchedulesParams>({
    numberOfInstallments: 3,
    intervalDays: 30,
  });

  // Load schedules and stats on mount
  useEffect(() => {
    loadSchedules(invoiceId);
    loadStats(invoiceId);
  }, [invoiceId, loadSchedules, loadStats]);

  /**
   * Handle generate schedules
   */
  const handleGenerate = async () => {
    try {
      await generateSchedules(invoiceId, generateParams);
      setShowGenerateModal(false);
      setGenerateParams({ numberOfInstallments: 3, intervalDays: 30 });
    } catch (err: any) {
      logger.error('Generate schedules error:', err);
    }
  };

  /**
   * Handle mark schedule as paid
   */
  const handleMarkAsPaid = async (scheduleId: string) => {
    const confirmed = await confirm({
      title: 'Marquer comme paye',
      message: 'Marquer cet echeancier comme paye ?',
      variant: 'info',
      confirmText: 'Confirmer',
      cancelText: 'Annuler',
    });
    if (confirmed) {
      try {
        await markAsPaid(scheduleId);
      } catch (err: any) {
        logger.error('Mark as paid error:', err);
      }
    }
  };

  /**
   * Handle delete schedule
   */
  const handleDelete = async (scheduleId: string) => {
    const confirmed = await confirm({
      title: 'Supprimer l\'echeancier',
      message: 'Supprimer cet echeancier ? Cette action est irreversible.',
      variant: 'danger',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
    });
    if (confirmed) {
      try {
        await deleteSchedule(scheduleId);
      } catch (err: any) {
        logger.error('Delete schedule error:', err);
      }
    }
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PAID':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-teal-100 text-teal-800">
            <CheckCircle2 className="w-3 h-3" />
            Payé
          </span>
        );
      case 'OVERDUE':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
            <AlertTriangle className="w-3 h-3" />
            En retard
          </span>
        );
      case 'PENDING':
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3" />
            En attente
          </span>
        );
    }
  };

  /**
   * Format currency
   */
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  /**
   * Format date
   */
  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-white/40">
        {/* Header - Teal Gradient Sticky */}
        <div className="sticky top-0 p-6 bg-gradient-to-r from-teal-600 to-teal-700 border-b border-teal-500/30 backdrop-blur-xl z-10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-light text-white flex items-center gap-3 tracking-tight">
                <Calendar className="w-8 h-8" />
                Échéanciers de paiement
              </h2>
              <p className="text-teal-50 mt-2 text-sm font-light">
                Facture {invoiceNumber} • Total: {formatCurrency(invoiceTotal)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-3 hover:bg-white/20 rounded-2xl transition-all duration-200 hover:scale-110"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Stats Bar - Glassmorphic 4 cards */}
        {stats && (
          <div className="p-6 bg-gradient-to-br from-gray-50/80 to-white/80 backdrop-blur-sm border-b border-gray-200/50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Total - utilise stats.totalCount calculé par le backend */}
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-5 border border-white/40 shadow-lg hover:bg-white/80 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.02), 0 2px 15px rgba(0,0,0,0.01)' }}>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Total</div>
                <div className="text-3xl font-bold text-gray-900">{stats.totalCount}</div>
              </div>

              {/* Payés (Teal) - utilise stats.paidAmount calculé par le backend */}
              <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 backdrop-blur-sm rounded-2xl p-5 border border-teal-200/50 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.02), 0 2px 15px rgba(0,0,0,0.01)' }}>
                <div className="text-xs font-semibold text-teal-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Payés
                </div>
                <div className="text-3xl font-bold text-teal-700">{stats.paid.count}</div>
                <div className="text-xs text-teal-600 font-medium mt-1">{formatCurrency(stats.paidAmount)}</div>
              </div>

              {/* En attente (Yellow) */}
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100/50 backdrop-blur-sm rounded-2xl p-5 border border-yellow-200/50 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.02), 0 2px 15px rgba(0,0,0,0.01)' }}>
                <div className="text-xs font-semibold text-yellow-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  En attente
                </div>
                <div className="text-3xl font-bold text-yellow-700">{stats.pending.count}</div>
              </div>

              {/* En retard (Red) */}
              <div className="bg-gradient-to-br from-red-50 to-red-100/50 backdrop-blur-sm rounded-2xl p-5 border border-red-200/50 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.02), 0 2px 15px rgba(0,0,0,0.01)' }}>
                <div className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  En retard
                </div>
                <div className="text-3xl font-bold text-red-700">{stats.overdue.count}</div>
              </div>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="p-6 border-b border-gray-200/50 bg-white/60 backdrop-blur-sm flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowGenerateModal(true)}
              disabled={saving || loading}
              className="inline-flex items-center justify-center font-semibold rounded-2xl
                transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed active:scale-95
                bg-gradient-to-r from-teal-600 to-teal-700 text-white
                hover:from-teal-700 hover:to-teal-800 hover:shadow-lg hover:-translate-y-0.5
                focus:ring-teal-500 shadow-lg shadow-teal-500/30
                px-5 py-2.5 text-base gap-2"
            >
              <Zap className="w-5 h-5" />
              Générer automatiquement
            </button>
            <button
              onClick={reload}
              disabled={loading || saving}
              className="px-5 py-2.5 text-gray-700 font-semibold bg-white/60 backdrop-blur-sm rounded-2xl hover:bg-white
                transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center gap-2 border border-gray-300/50 shadow-sm hover:shadow-md"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50/80 backdrop-blur-sm border-2 border-red-200 rounded-2xl flex justify-between items-center shadow-lg shadow-red-200/20">
            <div className="flex items-center gap-2 text-red-800 font-medium">
              <AlertTriangle className="w-5 h-5" />
              <span>{error}</span>
            </div>
            <button
              onClick={clearError}
              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-xl transition-all duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Schedules Table - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 bg-white/40 backdrop-blur-sm">
          {loading && schedules.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-teal-400 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Chargement des échéanciers...</p>
              </div>
            </div>
          ) : schedules.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-gray-100 to-gray-200/50 mb-6 shadow-lg">
                <Calendar className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-2">
                Aucun échéancier configuré
              </h3>
              <p className="text-gray-600 mb-8 font-light max-w-md">
                Générez automatiquement des échéanciers de paiement pour cette facture
              </p>
              <button
                onClick={() => setShowGenerateModal(true)}
                className="inline-flex items-center justify-center font-semibold rounded-2xl
                  transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2
                  disabled:opacity-50 disabled:cursor-not-allowed active:scale-95
                  bg-gradient-to-r from-teal-600 to-teal-700 text-white
                  hover:from-teal-700 hover:to-teal-800 hover:shadow-lg hover:-translate-y-0.5
                  focus:ring-teal-500 shadow-lg shadow-teal-500/30
                  px-6 py-3 text-base gap-2"
              >
                <Zap className="w-5 h-5" />
                Générer automatiquement
              </button>
            </div>
          ) : (
            <div className="bg-white/60 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden"
              style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.02), 0 2px 15px rgba(0,0,0,0.01)' }}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50/80 to-white/80 border-b border-gray-200/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        N°
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Date d'échéance
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Date de paiement
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white/40 backdrop-blur-sm divide-y divide-gray-200/50">
                    {schedules.map((schedule) => (
                      <tr
                        key={schedule.id}
                        className="hover:bg-white/60 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                          #{schedule.installmentNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {formatDate(schedule.dueDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                          <div className="flex items-center gap-1">
                            <Euro className="w-4 h-4 text-gray-400" />
                            {formatCurrency(schedule.amount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(schedule.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                          {schedule.paidAt ? formatDate(schedule.paidAt) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-2">
                            {schedule.status === 'PENDING' && (
                              <>
                                <button
                                  onClick={() => handleMarkAsPaid(schedule.id)}
                                  disabled={saving}
                                  className="px-3 py-2 text-sm font-semibold text-teal-700 bg-teal-50 rounded-xl
                                    hover:bg-teal-100 hover:scale-110 transition-all duration-200 flex items-center gap-1.5
                                    disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                  title="Marquer comme payé"
                                >
                                  <CheckCircle2 className="w-3 h-3" />
                                  Payé
                                </button>
                                <button
                                  onClick={() => handleDelete(schedule.id)}
                                  disabled={saving}
                                  className="p-2 text-red-700 bg-red-50 rounded-xl
                                    hover:bg-red-100 hover:scale-110 transition-all duration-200 flex items-center gap-1
                                    disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {schedule.status === 'PAID' && (
                              <span className="text-xs text-gray-400 italic font-medium">Payé</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Remaining Amount Display - Sticky Footer - utilise stats.remainingAmount calculé par le backend */}
        {stats && stats.remainingAmount > 0 && (
          <div className="sticky bottom-0 p-6 bg-gradient-to-r from-gray-50/90 to-white/90 backdrop-blur-xl border-t border-gray-200/50 shadow-lg">
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-semibold text-lg">Montant restant dû:</span>
              <span className="text-3xl font-bold text-gray-900">
                {formatCurrency(stats.remainingAmount)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Generate Modal - Glassmorphic */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fade-in">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-lg w-full border border-white/40">
            {/* Generate Modal Header */}
            <div className="p-6 bg-gradient-to-r from-teal-600 to-teal-700 rounded-t-3xl border-b border-teal-500/30">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-light text-white flex items-center gap-2 tracking-tight">
                  <Zap className="w-7 h-7" />
                  Générer des échéanciers
                </h3>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="p-2 hover:bg-white/20 rounded-2xl transition-all duration-200 hover:scale-110"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Generate Modal Body */}
            <div className="p-6 space-y-5">
              <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-5 border border-gray-200/50 shadow-sm">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre d'échéances
                </label>
                <input
                  type="number"
                  min="2"
                  max="12"
                  value={generateParams.numberOfInstallments}
                  onChange={(e) =>
                    setGenerateParams({
                      ...generateParams,
                      numberOfInstallments: parseInt(e.target.value) || 2,
                    })
                  }
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl
                    focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-medium text-gray-900"
                />
                <p className="text-xs text-gray-500 mt-2 font-medium">Entre 2 et 12 échéances</p>
              </div>

              <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-5 border border-gray-200/50 shadow-sm">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Intervalle (jours)
                </label>
                <select
                  value={generateParams.intervalDays}
                  onChange={(e) =>
                    setGenerateParams({
                      ...generateParams,
                      intervalDays: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl
                    focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-medium text-gray-900"
                >
                  <option value={7}>7 jours (hebdomadaire)</option>
                  <option value={15}>15 jours (bi-mensuel)</option>
                  <option value={30}>30 jours (mensuel)</option>
                  <option value={60}>60 jours (bimestriel)</option>
                  <option value={90}>90 jours (trimestriel)</option>
                </select>
              </div>

              {/* Preview */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-2 border-blue-200 rounded-2xl p-5 shadow-lg shadow-blue-200/20">
                <p className="text-sm text-blue-800 font-medium">
                  <strong className="font-bold">Aperçu:</strong> {generateParams.numberOfInstallments} échéances de{' '}
                  <span className="font-bold">{formatCurrency(invoiceTotal / generateParams.numberOfInstallments)}</span> tous les{' '}
                  {generateParams.intervalDays} jours
                </p>
              </div>

              {/* Warning */}
              {schedules.length > 0 && (
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100/50 border-2 border-yellow-200 rounded-2xl p-5 shadow-lg shadow-yellow-200/20">
                  <p className="text-sm text-yellow-800 font-medium">
                    <strong className="font-bold flex items-center gap-1.5 mb-1">
                      <AlertTriangle className="w-4 h-4" />
                      Attention
                    </strong>
                    Générer de nouveaux échéanciers supprimera les existants.
                  </p>
                </div>
              )}
            </div>

            {/* Generate Modal Actions */}
            <div className="p-6 bg-white/90 backdrop-blur-xl border-t border-gray-200/50 rounded-b-3xl flex gap-4">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="flex-1 px-5 py-3 text-gray-700 font-semibold bg-white hover:bg-gray-50 rounded-xl
                  transition-all duration-200 border border-gray-300 shadow-sm"
              >
                Annuler
              </button>
              <button
                onClick={handleGenerate}
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center font-semibold rounded-xl
                  transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2
                  disabled:opacity-50 disabled:cursor-not-allowed active:scale-95
                  bg-gradient-to-r from-teal-600 to-teal-700 text-white
                  hover:from-teal-700 hover:to-teal-800 hover:shadow-lg
                  focus:ring-teal-500 shadow-lg shadow-teal-500/30
                  px-5 py-3 gap-2"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Générer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
